import React from 'react';
import { motion } from 'framer-motion';
import { useInView } from 'react-intersection-observer';
import { ArrowUpRight, Send } from 'lucide-react';

// Telegram brand SVG icon
const TelegramIcon = ({ className = 'w-5 h-5' }) => (
  <svg viewBox="0 0 24 24" className={className} fill="currentColor">
    <path d="M9.78 18.65l.28-4.23 7.68-6.92c.34-.31-.07-.46-.52-.19L7.74 13.3 3.64 12c-.88-.25-.89-.86.2-1.3l15.97-6.16c.73-.33 1.43.18 1.15 1.3l-2.72 12.81c-.19.91-.74 1.13-1.5.71L12.6 16.3l-1.99 1.93c-.23.23-.42.42-.83.42z" />
  </svg>
);

const CONTACT_LANES = [
  {
    tag: 'General Enquiries',
    lead: 'Have a question about the summit, sponsorships, or the Zedit suite? Ping us directly.',
    quickPrompt: 'Hi Zedit team, I have a question about…',
  },
  {
    tag: 'Media & Press',
    lead: 'Interviews, quotes, event coverage, or newsroom features. We reply within 24h.',
    quickPrompt: 'Hi — I’m writing a piece on DeFi collaboration and…',
  },
  {
    tag: 'Partnerships',
    lead: 'Ecosystem collabs, cross-promo, co-marketing, or bespoke sponsor tiers.',
    quickPrompt: 'Hey, our protocol would love to partner on…',
  },
];

const TELEGRAM_URL = 'https://t.me/zedit_io';

const TelegramContactSection = () => {
  const [ref, inView] = useInView({ threshold: 0.15, triggerOnce: true });

  return (
    <section ref={ref} className="section-padding" data-testid="telegram-contact-section">
      <div className="container mx-auto px-4 sm:px-6">
        <div className="grid lg:grid-cols-[1.1fr_1fr] gap-6 md:gap-8 items-stretch">
          {/* LEFT — big Telegram card */}
          <motion.a
            href={TELEGRAM_URL}
            target="_blank"
            rel="noopener noreferrer"
            data-testid="telegram-hero-cta"
            initial={{ opacity: 0, y: 24 }}
            animate={inView ? { opacity: 1, y: 0 } : {}}
            transition={{ duration: 0.6 }}
            className="group relative rounded-3xl overflow-hidden border border-white/8 p-6 sm:p-8 lg:p-10 flex flex-col justify-between min-h-[380px]"
            style={{
              background:
                'radial-gradient(circle at 20% 10%, rgba(41,166,240,0.20) 0%, transparent 55%), linear-gradient(180deg, rgba(255,255,255,0.03) 0%, rgba(255,255,255,0.01) 100%)',
              backdropFilter: 'blur(14px)',
              WebkitBackdropFilter: 'blur(14px)',
            }}
          >
            {/* animated orbital dots */}
            <div className="absolute -top-24 -right-16 w-72 h-72 rounded-full blur-3xl pointer-events-none opacity-30" style={{ background: '#29A6F0' }} />
            <div className="absolute -bottom-32 -left-16 w-72 h-72 rounded-full blur-3xl pointer-events-none opacity-20" style={{ background: 'var(--accent-primary)' }} />

            <div className="relative">
              <div className="flex items-center gap-3 mb-5">
                <div
                  className="w-12 h-12 sm:w-14 sm:h-14 rounded-2xl flex items-center justify-center text-white shadow-lg"
                  style={{
                    background: 'linear-gradient(135deg, #29A6F0 0%, #0088CC 100%)',
                    boxShadow: '0 12px 30px -8px rgba(41,166,240,0.6)',
                  }}
                >
                  <TelegramIcon className="w-6 h-6 sm:w-7 sm:h-7" />
                </div>
                <div>
                  <div className="flex items-center gap-2">
                    <span className="text-xs uppercase tracking-widest text-[var(--accent-primary)] font-semibold">Direct line</span>
                    <span className="inline-flex items-center gap-1 text-[10px] font-bold text-green-400">
                      <span className="w-1.5 h-1.5 rounded-full bg-green-400 animate-pulse" />
                      Online now
                    </span>
                  </div>
                  <div className="text-[11px] sm:text-xs text-[var(--text-muted)] font-mono">@zedit_io</div>
                </div>
              </div>

              <h2 className="text-2xl sm:text-3xl md:text-4xl font-bold leading-tight mb-3">
                Skip the contact form.
                <br />
                <span className="text-[var(--accent-primary)]">DM us on Telegram.</span>
              </h2>
              <p className="text-sm sm:text-base text-[var(--text-secondary)] max-w-md mb-6">
                Direct chat with the team behind LayerSeek, Clips and TrueKOL. Typical reply under 4 hours during UTC business hours.
              </p>
            </div>

            {/* Realistic Telegram chat mockup */}
            <div className="relative">
              <div
                className="rounded-2xl overflow-hidden border border-white/10 backdrop-blur"
                style={{
                  background: 'linear-gradient(180deg, rgba(255,255,255,0.04) 0%, rgba(255,255,255,0.01) 100%)',
                }}
              >
                {/* Chat header */}
                <div className="flex items-center gap-2.5 px-3 py-2.5 border-b border-white/6">
                  <div
                    className="w-7 h-7 rounded-full flex items-center justify-center text-white"
                    style={{ background: 'linear-gradient(135deg, #29A6F0, #0088CC)' }}
                  >
                    <TelegramIcon className="w-3.5 h-3.5" />
                  </div>
                  <div className="min-w-0">
                    <div className="text-xs font-bold text-white">Zedit · Team</div>
                    <div className="text-[9px] text-[var(--text-muted)]">last seen just now</div>
                  </div>
                </div>
                {/* Messages */}
                <div className="p-3 space-y-2 text-[11px]">
                  <div className="max-w-[80%] rounded-2xl rounded-tl-sm px-3 py-2 text-white/90"
                       style={{ background: 'rgba(255,255,255,0.06)' }}>
                    Hey 👋 the Zedit team here. What are you working on?
                  </div>
                  <motion.div
                    animate={{ opacity: [0.4, 1, 0.4] }}
                    transition={{ duration: 1.4, repeat: Infinity }}
                    className="ml-auto max-w-[75%] rounded-2xl rounded-tr-sm px-3 py-2 text-[var(--bg-primary)] font-medium"
                    style={{ background: 'var(--accent-primary)' }}
                  >
                    Type your message…
                  </motion.div>
                </div>
              </div>
            </div>

            {/* CTA button */}
            <div className="relative mt-6 flex items-center justify-between">
              <span className="text-xs text-[var(--text-muted)]">Opens in Telegram</span>
              <span
                className="inline-flex items-center gap-2 px-5 py-2.5 rounded-full text-sm font-bold text-white transition-transform group-hover:scale-105"
                style={{
                  background: 'linear-gradient(135deg, #29A6F0 0%, #0088CC 100%)',
                  boxShadow: '0 10px 24px -6px rgba(41,166,240,0.5)',
                }}
              >
                <Send className="w-4 h-4" />
                Open chat
                <ArrowUpRight className="w-4 h-4" />
              </span>
            </div>
          </motion.a>

          {/* RIGHT — three intent lanes */}
          <div className="grid grid-cols-1 gap-3 sm:gap-4">
            {CONTACT_LANES.map((lane, i) => (
              <motion.a
                key={lane.tag}
                href={`${TELEGRAM_URL}?text=${encodeURIComponent(lane.quickPrompt)}`}
                target="_blank"
                rel="noopener noreferrer"
                data-testid={`telegram-lane-${lane.tag.toLowerCase().replace(/\s+/g,'-').replace(/&/g,'and')}`}
                initial={{ opacity: 0, x: 24 }}
                animate={inView ? { opacity: 1, x: 0 } : {}}
                transition={{ duration: 0.5, delay: 0.15 + i * 0.08 }}
                className="group relative rounded-2xl border border-white/8 p-5 sm:p-6 overflow-hidden transition-all hover:border-[var(--accent-primary)]/40"
                style={{
                  background: 'linear-gradient(135deg, rgba(255,255,255,0.03) 0%, rgba(255,255,255,0.01) 100%)',
                  backdropFilter: 'blur(10px)',
                  WebkitBackdropFilter: 'blur(10px)',
                }}
              >
                <div className="absolute inset-x-0 top-0 h-px bg-gradient-to-r from-transparent via-[var(--accent-primary)] to-transparent opacity-0 group-hover:opacity-100 transition-opacity" />

                <div className="flex items-start justify-between gap-3 mb-2">
                  <span className="inline-flex items-center gap-1.5 px-2.5 py-1 rounded-full text-[10px] sm:text-[11px] font-bold uppercase tracking-wider bg-[var(--accent-bg)] border border-[var(--accent-primary)]/30 text-[var(--accent-primary)]">
                    {lane.tag}
                  </span>
                  <ArrowUpRight className="w-4 h-4 text-white/30 group-hover:text-[var(--accent-primary)] group-hover:-translate-y-0.5 group-hover:translate-x-0.5 transition-all flex-shrink-0" />
                </div>
                <p className="text-sm text-white/85 mb-3 leading-relaxed">{lane.lead}</p>
                <div className="inline-flex items-center gap-2 text-[11px] text-[var(--text-muted)]">
                  <TelegramIcon className="w-3.5 h-3.5" />
                  <span className="italic truncate">“{lane.quickPrompt}”</span>
                </div>
              </motion.a>
            ))}
          </div>
        </div>
      </div>
    </section>
  );
};

export default TelegramContactSection;
